import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@familywallet/api-client';
import {
  type Account,
  addMoney,
  type DebtDetail,
  type DebtPaymentRequest,
  paidInOtherCurrency,
  parseRateInput,
} from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type FieldError, type Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { FormError } from '../components/FormError';
import { Chip, Row } from '../components/FormRow';
import { PageHeader } from '../components/PageHeader';
import { PickerSheet } from '../components/PickerSheet';
import { Button } from '../components/ui/button';
import { Card, ListGroup } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Segmented } from '../components/ui/segmented';
import { parsePositive } from '../lib/add-form';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { todayIn } from '../lib/dates';
import { type PaymentFormValues, paymentFormSchema } from '../lib/debt-form';
import { latestRateMap, useAccounts, useCurrentRates, useDebt } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey } from '../lib/members';
import { formatAmount, formatRateValue, groupAmountInput } from '../lib/money';

/**
 * Repay (borrowed) or record money received (lent), in the debt's own currency; a credit item
 * can also be paid in another family currency at that day's rate.
 */
export function DebtPayPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const me = useMe();
  const debt = useDebt(id);
  const accounts = useAccounts();
  const rates = useCurrentRates();
  if (!me.data || !debt.data || !accounts.data || !rates.data) {
    if (debt.isError || accounts.isError) {
      return (
        <Card role="alert">
          <p>{t('common.loadFailed')}</p>
        </Card>
      );
    }
    return <p role="status">{t('app.loading')}</p>;
  }
  const d = debt.data;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={d.direction === 'borrowed' ? t('debts.payTitle') : t('debts.collectTitle')}
        back={`/debts/${d.id}`}
      />
      <PaymentForm
        debt={d}
        accounts={accounts.data.items.filter((a) => a.canRecord && a.archivedAt === null)}
        familyCurrencies={me.data.family.currencies}
        baseCurrency={me.data.family.baseCurrency}
        timezone={me.data.family.timezone}
        latestRates={latestRateMap(rates.data)}
        myId={me.data.member.id}
      />
    </div>
  );
}

function PaymentForm({
  debt,
  accounts: allAccounts,
  familyCurrencies,
  baseCurrency,
  timezone,
  latestRates,
  myId,
}: {
  debt: DebtDetail;
  accounts: Account[];
  familyCurrencies: readonly string[];
  baseCurrency: string;
  timezone: string;
  latestRates: Record<string, string>;
  myId: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const [picker, setPicker] = useState<'accountId' | 'counterpartAccountId' | null>(null);
  const [rateWanted, setRateWanted] = useState(false);
  const today = todayIn(timezone);
  const currency = debt.currency;
  const state = location.state as {
    prefill?: Partial<PaymentFormValues>;
    installment?: number;
  } | null;
  const prefill = state?.prefill ?? {};
  // A credit item may be paid in another family currency, through the FX accounts.
  const [payCurrency, setPayCurrency] = useState(
    debt.credit && prefill.paidCurrency ? prefill.paidCurrency : currency,
  );
  const otherCurrency = payCurrency !== currency;
  // The one-off rate is for the currency that is not the base one.
  const rateCurrency = otherCurrency
    ? ([currency, payCurrency].find((c) => c !== baseCurrency) ?? currency)
    : currency;
  const foreign = rateCurrency !== baseCurrency;
  const askRate = foreign && (rateWanted || latestRates[rateCurrency] === undefined);
  const accounts = allAccounts.filter((a) => a.balances.some((b) => b.currency === payCurrency));
  const ctx = {
    currency,
    outstanding: debt.outstanding,
    internal: debt.internal,
    allowInterest: !debt.internal && debt.direction === 'borrowed',
    askRate,
  };
  // "Pay installment": the payment marks this installment paid.
  const installment = state?.installment;
  const usable = (id: string | null) => (id && accounts.some((a) => a.id === id) ? id : null);
  // Start from the account of the last payment on this debt, else the last one used on it,
  // else the debt owner's first account.
  const lastUsed = debt.history
    .flatMap((tx) => tx.lines)
    .find((l) => l.role === 'money' && l.accountId && accounts.some((a) => a.id === l.accountId));
  const startAccount =
    usable(debt.prefill.accountId) ??
    lastUsed?.accountId ??
    accounts.find((a) => a.ownerMemberId === debt.owner.memberId)?.id ??
    accounts.find((a) => a.ownerMemberId === myId)?.id ??
    '';

  const resolverRef = useRef<Resolver<PaymentFormValues, unknown, DebtPaymentRequest> | null>(null);
  const form = useForm<PaymentFormValues, unknown, DebtPaymentRequest>({
    resolver: (values, context, options) => resolverRef.current!(values, context, options),
    defaultValues: {
      amount: '',
      interest: '',
      accountId: startAccount,
      counterpartAccountId: usable(debt.prefill.counterpartAccountId) ?? '',
      occurredOn: today,
      note: '',
      rate: '',
      paidCurrency: '',
      ...prefill,
    },
  });
  const resolver = useMemo(
    () => zodResolver(paymentFormSchema(ctx)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [askRate, debt.outstanding],
  );
  const changePayCurrency = (next: string) => {
    setPayCurrency(next);
    form.setValue('paidCurrency', next === currency ? '' : next);
    const account = allAccounts.find((a) => a.id === form.getValues('accountId'));
    if (!account?.balances.some((b) => b.currency === next)) {
      const fallback = allAccounts.find((a) => a.balances.some((b) => b.currency === next));
      form.setValue('accountId', fallback?.id ?? '');
    }
  };
  useEffect(() => {
    resolverRef.current = resolver;
  }, [resolver]);
  const v = useWatch({ control: form.control }) as PaymentFormValues;
  const byId = new Map(accounts.map((a) => [a.id, a]));

  const pay = useMutation({
    mutationFn: (body: DebtPaymentRequest) => api.debts.pay(debt.id, body, { idempotencyKey }),
    onSuccess: async () => {
      renewKey();
      await invalidate();
      navigate(`/debts/${debt.id}`, { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'rate.required') setRateWanted(true);
    },
  });

  const errors = form.formState.errors;
  const errorText = (error: FieldError | undefined) =>
    error?.message ? t(error.message, { currency }) : null;
  const money = (amount: string) => formatAmount(amount, currency, i18n.language);
  const principal = parsePositive(v.amount ?? '', currency);
  const interest = ctx.allowInterest ? parsePositive(v.interest ?? '', currency) : null;
  const reformat = (name: 'amount' | 'interest') => {
    const parsed = parsePositive(form.getValues(name), currency);
    if (parsed) form.setValue(name, groupAmountInput(parsed, currency));
  };
  const other = debt.person.type === 'member' ? debt.person.name : '';
  // What leaves (or arrives in) the account in the other currency, at the latest family rate
  // (the API uses the rate on the payment day).
  const rateOf = (c: string) =>
    c === baseCurrency
      ? '1'
      : c === rateCurrency && askRate
        ? parseRateInput(v.rate ?? '')
        : (latestRates[c] ?? null);
  const value = principal ? (interest ? addMoney(principal, interest) : principal) : null;
  const debtRate = rateOf(currency);
  const paidRate = rateOf(payCurrency);
  const converted =
    otherCurrency && value && debtRate && paidRate
      ? paidInOtherCurrency(value, debtRate, paidRate, payCurrency)
      : null;
  const shownRate = rateOf(rateCurrency);

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={form.handleSubmit((body) =>
        pay.mutate(installment !== undefined ? { ...body, installment } : body),
      )}
    >
      <Card className="flex flex-col items-center gap-3">
        {installment !== undefined && (
          <span className="text-[13px] font-semibold">
            {t('debts.installmentNumber', { number: installment })}
          </span>
        )}
        <label htmlFor="pay-amount" className="text-[13px] text-muted">
          {t('debts.payAmount')}
        </label>
        <input
          id="pay-amount"
          inputMode={currency === 'LBP' ? 'text' : 'decimal'}
          autoComplete="off"
          dir="ltr"
          placeholder={currency === 'LBP' ? '500k' : '0.00'}
          aria-invalid={errors.amount ? true : undefined}
          className="w-full bg-transparent text-center text-[46px] leading-tight font-semibold tracking-[-1px] tabular-nums placeholder:text-muted focus-visible:outline-none"
          {...form.register('amount', { onBlur: () => reformat('amount') })}
        />
        <span className="rounded-full bg-surface-2 px-3 py-1 text-sm font-semibold">
          {currency}
        </span>
        <span className="text-xs text-muted" dir="ltr">
          {t('debts.maxHint', { amount: money(debt.outstanding) })}
        </span>
        <Button
          variant="ghost"
          onClick={() => form.setValue('amount', groupAmountInput(debt.outstanding, currency))}
        >
          {t('debts.payAll')}
        </Button>
        {errors.amount && <span className="text-sm text-danger">{errorText(errors.amount)}</span>}
      </Card>

      {debt.credit && familyCurrencies.length > 1 && (
        <div className="flex items-center justify-between gap-3 px-1">
          <span className="text-[13px] text-muted">
            {debt.direction === 'borrowed' ? t('credit.payIn') : t('credit.receiveIn')}
          </span>
          <div className="w-40">
            <Segmented
              label={debt.direction === 'borrowed' ? t('credit.payIn') : t('credit.receiveIn')}
              options={familyCurrencies.map((c) => ({ value: c, label: c }))}
              value={payCurrency}
              onChange={changePayCurrency}
            />
          </div>
        </div>
      )}
      {converted && (
        <p className="flex flex-col px-1 text-sm" aria-live="polite">
          <span className="font-medium" dir="ltr">
            {t(debt.direction === 'borrowed' ? 'credit.youPay' : 'credit.youGet', {
              amount: formatAmount(converted, payCurrency, i18n.language),
            })}
          </span>
          {shownRate && (
            <span className="text-xs text-muted">
              {t('credit.atRate', {
                base: baseCurrency,
                rate: formatRateValue(shownRate, i18n.language),
                quote: rateCurrency,
              })}
            </span>
          )}
        </p>
      )}

      <ListGroup>
        {ctx.allowInterest && (
          <Row
            label={t('debts.interest')}
            htmlFor="pay-interest"
            error={errorText(errors.interest)}
          >
            <input
              id="pay-interest"
              inputMode={currency === 'LBP' ? 'text' : 'decimal'}
              autoComplete="off"
              dir="ltr"
              placeholder={t('add.notePlaceholder')}
              className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] tabular-nums placeholder:text-muted focus-visible:outline-none"
              {...form.register('interest', { onBlur: () => reformat('interest') })}
            />
          </Row>
        )}
        <Row
          label={debt.direction === 'borrowed' ? t('debts.accountFrom') : t('debts.accountTo')}
          error={errorText(errors.accountId)}
        >
          <Chip onClick={() => setPicker('accountId')}>
            {byId.get(v.accountId)?.name ?? t('add.pickAccount')}
          </Chip>
        </Row>
        {debt.internal && (
          <Row
            label={t('debts.theirAccount', { name: other })}
            error={errorText(errors.counterpartAccountId)}
          >
            <Chip onClick={() => setPicker('counterpartAccountId')}>
              {byId.get(v.counterpartAccountId)?.name ?? t('add.pickAccount')}
            </Chip>
          </Row>
        )}
        <Row label={t('add.date')} htmlFor="pay-date" error={errorText(errors.occurredOn)}>
          <input
            id="pay-date"
            type="date"
            max={today}
            className="min-h-11 rounded-full bg-surface-2 px-3 text-sm font-medium text-fg focus-visible:outline-2 focus-visible:outline-accent"
            {...form.register('occurredOn')}
          />
        </Row>
        <Row label={t('debts.note')} htmlFor="pay-note">
          <input
            id="pay-note"
            autoComplete="off"
            maxLength={500}
            placeholder={t('add.notePlaceholder')}
            className="min-h-11 w-full min-w-0 bg-transparent text-end text-[15px] placeholder:text-muted focus-visible:outline-none"
            {...form.register('note')}
          />
        </Row>
      </ListGroup>
      {ctx.allowInterest && <p className="px-1 text-xs text-muted">{t('debts.interestHint')}</p>}
      {principal && interest && (
        <p className="px-1 text-sm font-medium" dir="ltr">
          {t('debts.totalPaid', { amount: money(addMoney(principal, interest)) })}
        </p>
      )}

      {askRate && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="pay-rate" className="text-[13px] font-medium text-muted">
            {t('rates.oneOffLabel', { base: baseCurrency, quote: rateCurrency })}
          </label>
          <Input
            id="pay-rate"
            inputMode="decimal"
            autoComplete="off"
            dir="ltr"
            placeholder="89,500"
            aria-invalid={errors.rate ? true : undefined}
            {...form.register('rate')}
          />
          {errors.rate && <p className="text-sm text-danger">{errorText(errors.rate)}</p>}
        </div>
      )}

      <FormError error={pay.error} />
      <Button type="submit" disabled={pay.isPending}>
        {pay.isPending ? t('common.saving') : t('debts.savePayment')}
      </Button>

      {picker && (
        <PickerSheet
          title={t('add.pickAccount')}
          options={accounts
            .filter((a) => a.id !== (picker === 'accountId' ? v.counterpartAccountId : v.accountId))
            .map((a) => ({
              value: a.id,
              label: a.name,
              hint: [
                a.ownerMemberId === null ? t('accountDetail.shared') : (a.ownerDisplayName ?? ''),
                a.balances
                  .filter((b) => b.currency === payCurrency)
                  .map((b) => formatAmount(b.balance, b.currency, i18n.language))
                  .join(' · '),
              ]
                .filter(Boolean)
                .join(' · '),
            }))}
          empty={t('add.noAccounts')}
          selected={v[picker]}
          onPick={(id) => {
            form.setValue(picker, id);
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </form>
  );
}
